import { Close as CloseIcon } from "@mui/icons-material";
import {
  Avatar,
  Box,
  Button,
  Fab,
  IconButton,
  Paper,
  TextField,
  Typography,
  Zoom,
} from "@mui/material";
import React from "react";
import { useTranslation } from "react-i18next";
import Markdown from "react-markdown";
import useAI from "src/hooks/useAI";
import { setAIState } from "src/slices/ai";
import { useDispatch, useSelector } from "src/store";

const AIChatBot = ({ role }) => {
  const { t } = useTranslation();
  const { messages, iaOpen, loadingIA } = useSelector((state) => state.ai);
  const dispatch = useDispatch();
  const { adminMode } = useSelector((state) => state.settings);
  const { chatMessage, generateContent } = useAI();
  const initialized = React.useRef(false);
  const { currentCustomer } = useSelector((state) => state.customers);
  const [message, setMessage] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [isMinimized, setIsMinimized] = React.useState(true);
  const [isHovered, setIsHovered] = React.useState(false);

  const handleSendMessage = React.useCallback(async () => {
    if (!message.trim()) return;

    setLoading(true);
    const currentMessage = message;
    setMessage("");

    try {
      if (role !== "chat") {
        await generateContent(currentMessage, role);
      } else {
        await chatMessage(currentMessage, role);
      }
    } finally {
      setLoading(false);
    }
  }, [message, role, generateContent, chatMessage]);

  React.useEffect(() => {
    initialized.current = true;
  }, []);

  if (!adminMode) return null;

  if (!iaOpen) {
    return (
      <Zoom in timeout={300}>
        <Fab
          variant="extended"
          color="primary"
          onClick={() => dispatch(setAIState({ iaOpen: true }))}
          sx={{
            position: "fixed",
            bottom: { xs: 16, md: 24 },
            right: { xs: 16, md: 24 },
            zIndex: 9998,
          }}
        >
          <Avatar
            src={
              currentCustomer?.apps_config?.chatBot?.icon ||
              "/static/icons/chat_bot_icon.webp"
            }
            alt="icon"
            sx={{ width: 32, height: 32, mr: 1 }}
          />
          {currentCustomer?.apps_config?.chatBot?.buttonText || t("Chat")}
        </Fab>
      </Zoom>
    );
  }

  if (isMinimized) {
    return (
      <Zoom in timeout={300}>
        <Box
          onClick={() => setIsMinimized(false)}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          sx={{
            position: "fixed",
            bottom: { xs: 16, md: 24 },
            right: { xs: 16, md: 24 },
            zIndex: 9999,
            cursor: "pointer",
            transform: isHovered ? "scale(1.1)" : "scale(1)",
            transition: "transform 0.2s ease-in-out",
          }}
        >
          <Avatar
            src={
              currentCustomer?.apps_config?.chatBot?.icon ||
              "/static/icons/chat_bot_icon.webp"
            }
            alt="icon"
            sx={{
              width: { xs: 56, md: 64 },
              height: { xs: 56, md: 64 },
              boxShadow: 3,
              border: "3px solid",
              borderColor: "primary.main",
            }}
          />
        </Box>
      </Zoom>
    );
  }

  return (
    <Zoom in timeout={400}>
      <Paper
        elevation={8}
        sx={{
          position: "fixed",
          bottom: { xs: 8, md: 20 },
          right: { xs: 8, md: 20 },
          width: { xs: "calc(100vw - 16px)", sm: 420, md: 460 },
          height: { xs: "calc(100vh - 16px)", sm: 600, md: 650 },
          display: "flex",
          flexDirection: "column",
          borderRadius: { xs: 2, md: 3 },
          overflow: "hidden",
          zIndex: 9999,
        }}
      >
        {/* Header */}
        <Box
          sx={{
            p: { xs: 1.5, md: 2 },
            borderBottom: 1,
            borderColor: "divider",
            bgcolor: "primary.main",
            color: "primary.contrastText",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              src={
                currentCustomer?.apps_config?.chatBot?.icon ||
                "/static/icons/chat_bot_icon.webp"
              }
              alt="icon"
              sx={{ width: { xs: 40, md: 48 }, height: { xs: 40, md: 48 } }}
            />
            <Typography
              variant="h6"
              sx={{ fontSize: { xs: "1rem", md: "1.25rem" } }}
            >
              {currentCustomer?.apps_config?.chatBot?.title ||
                t("Gestión Ciudad IA")}
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 0.5 }}>
            <IconButton
              size="small"
              onClick={() => setIsMinimized(true)}
              sx={{ color: "inherit" }}
              title={t("Minimizar")}
            >
              <Box
                sx={{
                  width: 20,
                  height: 2,
                  bgcolor: "currentColor",
                  borderRadius: 1,
                }}
              />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => dispatch(setAIState({ iaOpen: false }))}
              sx={{ color: "inherit" }}
              title={t("Cerrar")}
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>

        {/* Messages Area */}
        <Box
          sx={{
            flex: 1,
            overflowY: "scroll",
            p: { xs: 1.5, md: 2 },
            bgcolor: "grey.50",
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              backgroundColor: "transparent",
            },
            "&::-webkit-scrollbar-thumb": {
              backgroundColor: "rgba(0,0,0,0.2)",
              borderRadius: "3px",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.3)",
              },
            },
            maxHeight: {
              xs: "50vh !important",
              sm: "40vh!important",
              md: "55vh !important",
            },
          }}
        >
          {loadingIA && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center" }}
            >
              {t("Cargando...")}
            </Typography>
          )}
          {messages.length === 0 && !loadingIA && (
            <ChatMessageWidget
              m={{
                from: "ia",
                message:
                  currentCustomer?.apps_config?.chatBot?.emptyStateText ||
                  t("Hola, ¿en qué puedo ayudarte?"),
                timestamp: Date.now(),
              }}
            />
          )}
          {messages.map((m, i) => (
            <ChatMessageWidget key={`${m.timestamp}-${i}`} m={m} />
          ))}
        </Box>

        {/* Input Area */}
        <Box
          component="form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSendMessage();
          }}
          sx={{
            p: { xs: 1.5, md: 2 },
            borderTop: 1,
            borderColor: "divider",
            bgcolor: "background.paper",
            display: "flex",
            gap: 1,
            alignItems: "flex-end",
            flexShrink: 0,
          }}
        >
          <TextField
            fullWidth
            placeholder={
              role === "redactor"
                ? t("Escribe tu texto para redactar...")
                : t("Escribe un mensaje...")
            }
            multiline
            maxRows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            disabled={loading}
            size="small"
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: 2,
                fontSize: { xs: "0.875rem", md: "1rem" },
              },
            }}
          />
          <Button
            type="submit"
            variant="contained"
            disabled={!message.trim() || loading}
            sx={{
              minWidth: { xs: 70, md: 90 },
              height: 40,
              borderRadius: 2,
              fontSize: { xs: "0.813rem", md: "0.875rem" },
            }}
          >
            {role === "redactor" ? t("Redactar") : t("Enviar")}
          </Button>
        </Box>
      </Paper>
    </Zoom>
  );
};

export default React.memo(AIChatBot);

const ChatMessageWidget = React.memo(({ m }) => {
  const [displayedMessage, setDisplayedMessage] = React.useState("");
  const messageEndRef = React.useRef(null);
  const isUser = m.from === "user";
  const fullMessage = m.message || "";

  React.useEffect(() => {
    if (isUser) {
      setDisplayedMessage(fullMessage);
      return;
    }

    let index = 0;
    setDisplayedMessage("");

    const timer = setInterval(() => {
      if (index < fullMessage.length) {
        setDisplayedMessage((prev) => prev + fullMessage[index]);
        index++;
        messageEndRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "end",
        });
      } else {
        clearInterval(timer);
      }
    }, 15);

    return () => clearInterval(timer);
  }, [fullMessage, isUser]);

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: isUser ? "flex-end" : "flex-start",
        px: { xs: 0.5, md: 1 },
      }}
    >
      <Paper
        elevation={1}
        sx={{
          maxWidth: "75%",
          p: { xs: 1.5, md: 2 },
          borderRadius: 2,
          bgcolor: isUser ? "primary.main" : "background.paper",
          color: isUser ? "primary.contrastText" : "text.primary",
          borderBottomRightRadius: isUser ? 4 : 16,
          borderBottomLeftRadius: isUser ? 16 : 4,
        }}
      >
        <Typography
          variant="body2"
          component="div"
          sx={{
            fontSize: { xs: "0.875rem", md: "0.938rem" },
            lineHeight: 1.6,
            wordBreak: "break-word",
            "& p": { margin: 0 },
            "& p + p": { mt: 1 },
            "& code": {
              bgcolor: isUser ? "rgba(0,0,0,0.1)" : "grey.100",
              px: 0.5,
              py: 0.25,
              borderRadius: 0.5,
              fontSize: "0.875em",
            },
          }}
        >
          <Markdown>{isUser ? fullMessage : displayedMessage}</Markdown>
        </Typography>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mt: 0.5,
            fontSize: { xs: "0.688rem", md: "0.75rem" },
            opacity: 0.7,
            textAlign: isUser ? "right" : "left",
          }}
        >
          {new Date(m.timestamp || Date.now()).toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </Typography>
        <div ref={messageEndRef} />
      </Paper>
    </Box>
  );
});

ChatMessageWidget.displayName = "ChatMessageWidget";
